import React, {useCallback, useContext, useEffect, useState} from 'react'
import {Col, Form, Row} from "react-bootstrap";
import {useHistory} from "react-router-dom";
import {WireTemplatesContext} from "./context";
import {SecurityContext} from "context/security";
import * as URL from "service/UrlConstant";
import WireTemplateWizardLayout from "./WireTemplateWizardLayout";
import ShiftEnterPopupMessage from "components/ShiftEnterPopupMessage";
import {
    getWireTemplateStepUrl,
    shouldNavigateToWireTemplateStep,
    WIRE_TEMPLATE_STEP_URL
} from "./AddNewWireTemplateStep1";

const creditorFields = [
    [
        {label: 'Creditor BIC', name: 'creditorBIC', md: 3, maxLength: 50},
        {label: 'Creditor Account IBAN', name: 'creditorAccountIBAN', md: 2, maxLength: 50, required: true},
        {label: 'Creditor Name', name: 'creditorName', md: 3, maxLength: 255, required: true},
        {label: 'Creditor Department', name: 'creditorDepartment', md: 4, maxLength: 70},
    ],
    [
        {label: 'Sub Department', name: 'creditorSubDepartment', md: 3, maxLength: 70},
        {label: 'Street Name', name: 'creditorStreetName', md: 4, maxLength: 70},
        {label: 'Building Number', name: 'creditorBuildingNumber', md: 2, maxLength: 16},
        {label: 'Building Name', name: 'creditorBuildingName', md: 3, maxLength: 35},
    ],
    [
        {label: 'Floor', name: 'creditorFloor', md: 2, maxLength: 70},
        {label: 'Post Box', name: 'creditorPostBox', md: 2, maxLength: 16},
        {label: 'Room', name: 'creditorRoom', md: 2, maxLength: 70},
        {label: 'Post Code', name: 'creditorPostCode', md: 2, maxLength: 16},
        {label: 'Town Name', name: 'creditorTownName', md: 4, maxLength: 35, required: true},
    ],
    [
        {label: 'Town Location Name', name: 'creditorTownLocationName', md: 4, maxLength: 35},
        {label: 'District Name', name: 'creditorDistrictName', md: 4, maxLength: 35},
        {label: 'Province', name: 'creditorProvince', md: 2, maxLength: 35},
        {label: 'Country', name: 'creditorCountry', md: 2, maxLength: 2, type: 'country', required: true},
    ],
];

const CREDITOR_IDENTIFIER_ERROR = 'ERROR. Please check the Note above to fill in the required fields.';
const hasValue = (value) => value !== undefined && value !== null && value.toString().trim() !== '';

export const validateStep3 = (addWireTemplateState = {}) => {
    let errors = {}
    const {
        creditorAccountIBAN,
        creditorName,
        creditorTownName,
        creditorCountry
    } = addWireTemplateState;

    if (!hasValue(creditorAccountIBAN)) {
        errors['creditorAccountIBAN'] = CREDITOR_IDENTIFIER_ERROR
    }

    if (!hasValue(creditorName)) {
        errors['creditorName'] = CREDITOR_IDENTIFIER_ERROR
    }

    if (!hasValue(creditorTownName)) {
        errors['creditorTownName'] = CREDITOR_IDENTIFIER_ERROR
    }

    if(!hasValue(creditorCountry)) {
        errors['creditorCountry'] = CREDITOR_IDENTIFIER_ERROR
    }

    return errors
}

export default function AddNewWireTemplateStep3() {

    const history = useHistory();
    const {addWireTemplateState, setAddWireTemplateState, clearAddNewWireTemplateState} = useContext(WireTemplatesContext);
    const {makeGetRequestNoParams} = useContext(SecurityContext);
    const [countries, setCountries] = useState([]);
    const [formErrors, setFormErrors] = useState({});

    const currentStepPage = '3'
    const backStepPage = '2'
    const nextStepPage = '4'

    const getCountries = useCallback(() => {
        const onSuccessResponse = (res) => {
            if (res.data) {
                setCountries(res.data);
            }
        }

        const onErrorResponse = () => {}

        makeGetRequestNoParams(URL.COUNTRY_LIST_COUNTRY_NAME, onSuccessResponse, onErrorResponse)
    }, [makeGetRequestNoParams])

    useEffect(() => {
        getCountries()
    }, [getCountries])

    const handleChange = (e) => {
        const {name, value} = e.currentTarget;
        setAddWireTemplateState({...addWireTemplateState, [name]: value});
        if (formErrors[name]) {
            setFormErrors(previousErrors => ({...previousErrors, [name]: ''}))
        }
    }
    useEffect(() => {
        if (shouldNavigateToWireTemplateStep(currentStepPage, addWireTemplateState?.newPageStep)) {
            history.push(getWireTemplateStepUrl(addWireTemplateState.newPageStep))
        }
    }, [addWireTemplateState?.newPageStep, history])

    const handleClear = (e) => {
        e.preventDefault();
        clearAddNewWireTemplateState();
        setFormErrors({});
    }

    const handleBack = (e) => {
        e.preventDefault();
        const newAddWireTemplateState = {...addWireTemplateState}
        newAddWireTemplateState['newPageStep']=backStepPage
        setAddWireTemplateState(newAddWireTemplateState)
        history.push(WIRE_TEMPLATE_STEP_URL + backStepPage);
    }

    const handleNext = (e) => {
        e.preventDefault();

        const errors = validateStep3(addWireTemplateState);
        if (Object.keys(errors).length !== 0) {
            setFormErrors(errors);
            return
        }

        const newAddWireTemplateState = {...addWireTemplateState}
        newAddWireTemplateState['newPageStep']=nextStepPage
        setAddWireTemplateState(newAddWireTemplateState)
        history.push(WIRE_TEMPLATE_STEP_URL + nextStepPage);
    }

    const renderRequiredMarker = (field) => {
        if (field.required) {
            return <span className='red-cell'>*</span>;
        }

        return field.conditionallyRequired ? <span className='grey-cell'>*</span> : null;
    };

    const renderField = (field) => (
        <Col md={field.md} key={field.name}>
            <Form.Group controlId={`formWireTemplateStep3${field.name}`} className={field.type === 'country' ? 'td-select' : ''}>
                <Form.Label column={false} className="filter-input-label">{field.label} {renderRequiredMarker(field)}</Form.Label>
                {field.type === 'country' ?
                    <Form.Control as="select" name={field.name} value={addWireTemplateState[field.name] || ''} onChange={handleChange}
                                  isInvalid={!!formErrors[field.name]}>
                        <option value={''}>Select</option>
                        {countries.map(country => (
                            <option key={country.countryCode} value={country.countryCode}>{country.country}</option>
                        ))}
                    </Form.Control> :
                    <Form.Control
                        name={field.name}
                        value={addWireTemplateState[field.name] || ''}
                        maxLength={field.maxLength}
                        onChange={handleChange}
                        isInvalid={!!formErrors[field.name]}
                    />
                }
                <Form.Control.Feedback type={'invalid'}>
                    <b><i className="bi bi-exclamation-circle"></i>{formErrors[field.name]}</b>
                </Form.Control.Feedback>
            </Form.Group>
        </Col>
    )

    const hasFormErrors = Object.keys(formErrors).length > 0;

    return (
        <WireTemplateWizardLayout currentStep={3} stepTitle="Creditor Details">
            <ShiftEnterPopupMessage />
            <Form>
                <div className={`awt-note ${hasFormErrors ? 'awt-note--error' : ''}`} role="note">
                    <b>Note:</b> Please populate Creditor Street Name and Creditor Province where possible.
                    <p></p>
                    <b>Note:</b> Please populate the mandatory fields CreditorAccountIBAN, CreditorName, CreditorTownName, and CreditorCountry.
                </div>
                {creditorFields.map((fieldRow, index) => (
                    <Row key={index}>
                        {fieldRow.map(renderField)}
                    </Row>
                ))}

                <div className="awt-actions">
                    <button className="btn td-btn-secondary-clear" onClick={handleClear}>Clear</button>
                    <button className="btn td-btn-secondary-clear" onClick={handleBack}>Back</button>
                    <button className="btn td-btn-primary-light" onClick={handleNext}>Next</button>
                </div>
            </Form>
        </WireTemplateWizardLayout>
    )
}

